// @ts-check

/**
 * @file Renders the projects index route.
 */

import {FlashNotifications} from "flash-notifications"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useRouter} from "expo-router"
import {memo, useEffect, useMemo} from "react"
import useLiveQuery from "velocious/build/src/database/use-live-query.js"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import useBreakpoint from "responsive-breakpoints/build/use-breakpoint.js"
import {ActivityIndicator} from "react-native"
import {View} from "react-native-propforge"
import Link from "../../src/components/link"
import Button from "../../src/components/button"
import buildRoutePath from "../../src/shared/build-route-path"
import Card from "../../src/components/card"
import {useScreenLoading} from "../../src/components/board-layout/screen-loading"
import confirmModal from "../../src/shared/confirm-modal"
import EmptyState from "../../src/components/empty-state"
import LabeledSelect from "../../src/components/labeled-select"
import LabeledTextInput from "../../src/components/labeled-text-input"
import LoadingState from "../../src/components/loading-state"
import PageHeader from "../../src/components/page-header"
import Pagination from "../../src/components/pagination"
import {Project as RemoteProject} from "../../src/frontend-models/project"
import {localCountSource} from "../../src/database/local-live-query"
import Project from "../../src/models/project"
import {useProjectSyncOnline} from "../../src/sync/project-sync-connectivity"
import {projectSyncLifecycle} from "../../src/sync/project-sync-lifecycle"
import {pushQueryParamUpdates} from "on-location-changed/build/query-param-updates"
import useCurrentQueryParams from "on-location-changed/build/use-current-query-params"
import useSelectedQueryParams from "on-location-changed/build/use-selected-query-params"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import ScreenTitle from "../../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {Record<string, string | string[] | undefined>} RouteQueryParams */
/** @import {BoardThemeColors} from "../../src/shared/theme" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {"active" | "finished" | "inactive"} ProjectStateFilter */

/** @type {ProjectStateFilter[]} */
const DEFAULT_STATE_FILTERS = ["active"]
const PAGE_SIZE = 30
const PROJECT_STATE_FILTER_ALL = "all"
const PROJECTS_QUERY_PARAM_SELECTOR = ["state"]

/**
 * Resolves background + text tokens for a project state badge from
 * the active palette. Active stays green, finished stays blue,
 * inactive stays neutral — the palette just provides legible
 * variants in both light and dark mode.
 * @param {string} state Project state value (`active`, `finished`, `inactive`, …).
 * @param {BoardThemeColors} colors Palette returned from `useThemeColors`.
 * @returns {{background: string, text: string}} Background fill and text color for the badge pill.
 */
function projectStateBadgeColors(state, colors) {
  if (state === "finished") {
    return {background: colors.infoBackground, text: colors.infoText}
  }
  if (state === "inactive") {
    return {background: colors.surfaceMuted, text: colors.textMuted}
  }
  return {background: colors.successBackground, text: colors.successText}
}

/** @typedef {{[key: string]: unknown}} ProjectsIndexScreenProps */

/**
 * @typedef {object} ProjectsIndexScreenState
 * @property {boolean} deleting Whether a delete request is running.
 * @property {number} page Current page number.
 * @property {string} searchQuery Current project search query.
 * @property {ProjectStateFilter[]} stateFilters Selected project state filters; empty means all states.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 */

/**
 * Lists projects with search, pagination, and delete actions.
 * @augments {ShapeComponent<ProjectsIndexScreenProps, ProjectsIndexScreenState>}
 */
class ProjectsIndexScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useBreakpoint>} */
  breakpoint = this.hookField()
  /** @type {() => RouteQueryParams} */
  currentQueryParams
  /** @type {RouteQueryParams} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {InstanceType<typeof Project>[]} */
  projects = []
  readError = /** @type {Error | null} */ (null)
  readLoading = true
  online = true
  totalCount = 0
  searchDebounceTimer = /** @type {ReturnType<typeof setTimeout> | null} */ null
  /** @type {ProjectsIndexScreenState} */
  state = {
    deleting: false,
    page: 1,
    searchQuery: "",
    stateFilters: /** @type {ProjectStateFilter[]} */ ([...DEFAULT_STATE_FILTERS]),
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null
  }

  /** Initializes translations, params, and project loading effects. */
  setup() {
    useLocale()
    const params = /** @type {RouteQueryParams} */ (useSelectedQueryParams(PROJECTS_QUERY_PARAM_SELECTOR))
    const currentQueryParams = /** @type {() => RouteQueryParams} */ (useCurrentQueryParams())
    const router = useRouter()
    const routeStateFilters = stateFiltersFromParams(params)

    this.breakpoint = useBreakpoint()
    this.currentQueryParams = currentQueryParams
    this.params = params
    this.router = router
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    this.online = useProjectSyncOnline()
    if (!sameStringArray(this.state.stateFilters, routeStateFilters)) {
      this.state.page = 1
      this.state.stateFilters = routeStateFilters
    }
    const page = this.s.page
    const searchQuery = this.s.searchQuery
    const stateFilters = this.s.stateFilters
    const stateFiltersKey = stateFilters.join(",")
    const queryParams = useMemo(() => projectQueryParams({
      searchQuery,
      stateFilters: stateFiltersKey ? /** @type {ProjectStateFilter[]} */ (stateFiltersKey.split(",")) : []
    }), [searchQuery, stateFiltersKey])
    const projectsQuery = useMemo(
      () => Project.ransack(queryParams).page(page).perPage(PAGE_SIZE),
      [page, queryParams]
    )
    const countSource = useMemo(() => localCountSource({
      modelClass: Project,
      query: (projects) => projects.ransack(queryParams)
    }), [queryParams])
    const projectsState = useLiveQuery(projectsQuery)
    const countState = useLiveQuery(countSource)

    this.projects = projectsState.results
    this.totalCount = countState.results[0] ?? 0
    this.readError = projectsState.error || countState.error
    this.readLoading = projectsState.loading || countState.loading
    useScreenLoading(this.readLoading && this.projects.length === 0)
    useEffect(() => {
      return () => {
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      }
    }, [])
  }

  /**
   * Renders the projects index screen.
   * @returns {ReactNode} Projects index content.
   */
  render() {
    const {status} = this.s
    const visibleStatus = status || (this.readError ? {kind: "error", text: this.readError.message} : null)
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="projectsIndexScreen"
      >
        <ScreenTitle>{_("Projects")}</ScreenTitle>
        <View
          testID="projectsIndexScreen/content"
        >
          <PageHeader
            actions={
              <Button
                disabled={!this.online}
                href={buildRoutePath(this.tt.params, "/projects/new")}
                iconName="plus"
                label={_("New project")}
                testID="projectsIndexScreen/newButton"
              />
            }
            subtitle={_("Browse, search and manage projects")}
            testID="projectsIndexScreen/header"
            title={_("Projects")}
          />
          {this.renderFilters()}
          {visibleStatus &&
            <Text
              style={styles[`status-${this.tt.themeName}-${visibleStatus.kind}`] ||= {
                color: visibleStatus.kind === "error" ? this.tt.colors.dangerText : this.tt.colors.successText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="projectsIndexScreen/status"
            >
              {visibleStatus.text}
            </Text>
          }
          {this.renderProjectsResults()}
          {this.renderPagination()}
        </View>
      </View>
    )
  }

  /**
   * Renders project search and state filters.
   * @returns {ReactNode} Filter controls.
   */
  renderFilters() {
    const {searchQuery, stateFilters} = this.s
    const breakpoint = this.tt.breakpoint
    const breakpointName = breakpoint.name
    return (
      <View
        style={styles[`filters-${breakpointName}`] ||= breakpoint.styling({
          base: {
            alignItems: "stretch",
            flexDirection: "column",
            gap: 12,
            marginBottom: 16
          },
          mdUp: {
            alignItems: "flex-end",
            flexDirection: "row",
            flexWrap: "wrap"
          }
        })}
        testID="projectsIndexScreen/filters"
      >
        <View
          style={styles[`searchField-${breakpointName}`] ||= breakpoint.styling({
            base: {
              width: "100%"
            },
            mdUp: {
              flexBasis: 260,
              flexGrow: 2,
              minWidth: 220,
              width: undefined
            }
          })}
          testID="projectsIndexScreen/filters/searchField"
        >
          <LabeledTextInput
            defaultValue={searchQuery}
            label={_("Search")}
            onChangeText={this.tt.onSearchChangeText}
            placeholder={_("Search projects by name...")}
            testID="projectsIndexScreen/searchInput"
          />
        </View>
        <View
          style={styles[`stateField-${breakpointName}`] ||= breakpoint.styling({
            base: {
              width: "100%"
            },
            mdUp: {
              flexBasis: 180,
              flexGrow: 1,
              minWidth: 170,
              width: undefined
            }
          })}
          testID="projectsIndexScreen/filters/stateField"
        >
          <LabeledSelect
            label={_("State")}
            multiple
            onChangeValue={this.tt.onStateFilterChangeValue}
            options={projectStateFilterOptions()}
            placeholder={_("All states")}
            testID="projectsIndexScreen/filters/stateSelect"
            values={stateFilters}
          />
        </View>
      </View>
    )
  }

  /**
   * Renders pagination for loaded results.
   * @returns {ReactNode} Pagination controls or null while loading.
   */
  renderPagination() {
    const {page} = this.s
    if (this.readLoading) return null
    return (
      <Pagination
        onPageChange={this.tt.onPageChange}
        page={page}
        pageSize={PAGE_SIZE}
        testID="projectsIndexScreen/pagination"
        totalCount={this.totalCount}
      />
    )
  }

  /**
   * Renders the project results or a results-only loading indicator.
   * @returns {ReactNode} Project results content.
   */
  renderProjectsResults() {
    if (this.readLoading) {
      return (
        <View
          style={styles[`resultsLoading-${this.tt.themeName}`] ||= {
            alignItems: "center",
            backgroundColor: this.tt.colors.surface,
            borderColor: this.tt.colors.border,
            borderRadius: 8,
            borderWidth: 1,
            gap: 10,
            justifyContent: "center",
            marginBottom: 16,
            minHeight: 160,
            paddingVertical: 28
          }}
          testID="projectsIndexScreen/resultsLoading"
        >
          <ActivityIndicator
            color={this.tt.colors.link}
            size="small"
            testID="projectsIndexScreen/resultsLoading/indicator"
          />
          <LoadingState testID="projectsIndexScreen/loadingText" />
        </View>
      )
    }
    return this.renderProjectsList()
  }

  /**
   * Renders the project list or empty state.
   * @returns {ReactNode} Project list content.
   */
  renderProjectsList() {
    const projects = this.projects
    if (projects.length === 0) {
      return (
        <EmptyState
          message={_("No projects found")}
          testID="projectsIndexScreen/emptyCard"
        />
      )
    }
    return (
      <View testID="projectsIndexScreen/list">
        {projects.map((project) => this.renderProjectCard(project))}
      </View>
    )
  }

  /**
   * Renders a single project card.
   * @param {InstanceType<typeof Project>} project Project record to render.
   * @returns {ReactNode} Rendered card content.
   */
  renderProjectCard(project) {
    const projectId = String(project.id())
    const state = project.state() || "active"
    const themeName = this.tt.themeName
    const badgeColors = projectStateBadgeColors(state, this.tt.colors)
    const stateLabel = translateState(_, state)
    return (
      <Card
        key={projectId}
        style={styles.projectCardSpacing ||= {marginBottom: 10}}
        testID={`projectsIndexScreen/project-${projectId}/card`}
      >
        <View
          style={styles.projectCardHeader ||= {
            alignItems: "center",
            flexDirection: "row",
            gap: 8,
            justifyContent: "space-between"
          }}
          testID={`projectsIndexScreen/project-${projectId}/header`}
        >
          <Link
            href={buildRoutePath(this.tt.params, `/projects/${projectId}`)}
            style={styles.projectCardLink ||= {
              flex: 1
            }}
            testID={`projectsIndexScreen/project-${projectId}/link`}
          >
            <Text
              style={styles[`projectName-${themeName}`] ||= {
                fontSize: 16,
                fontWeight: 700
              }}
              testID={`projectsIndexScreen/project-${projectId}/name`}
            >
              {project.name()}
            </Text>
          </Link>
          <View
            style={styles[`stateBadge-${themeName}-${state}`] ||= {
              backgroundColor: badgeColors.background,
              borderRadius: 999,
              paddingHorizontal: 10,
              paddingVertical: 4
            }}
            testID={`projectsIndexScreen/project-${projectId}/badge`}
          >
            <Text
              style={styles[`stateBadgeText-${themeName}-${state}`] ||= {
                color: badgeColors.text,
                fontSize: 12,
                fontWeight: 700
              }}
              testID={`projectsIndexScreen/project-${projectId}/badge/text`}
            >
              {stateLabel}
            </Text>
          </View>
          <Button
            disabled={!this.online}
            href={buildRoutePath(this.tt.params, `/projects/${projectId}/edit`)}
            iconName="pen-to-square"
            label={_("Edit")}
            testID={`projectsIndexScreen/project-${projectId}/editButton`}
            variant="outline"
          />
          <Button
            disabled={!this.online || this.s.deleting}
            iconName="trash"
            label={_("Delete")}
            onPress={() => this.tt.onDeletePress(project.id())}
            testID={`projectsIndexScreen/project-${projectId}/deleteButton`}
            variant="destructive-outline"
          />
        </View>
      </Card>
    )
  }

  /**
   * Confirms and deletes a project.
   * @param {string} projectId Project id to delete.
   * @returns {Promise<void>} Resolves when the delete flow finishes.
   */
  onDeletePress = async (projectId) => {
    if (!this.assertOnline()) return
    const project = this.projects.find((candidate) => candidate.id() === projectId)
    const projectName = project?.name() ?? ""
    const ok = await confirmModal(
      _("Delete project: %{name}?", {
        name: projectName
      }),
      {
        cancelLabel: _("Cancel"),
        confirmLabel: _("Delete"),
        title: _("Confirm delete"),
        tone: "destructive"
      }
    )
    if (!ok) return
    this.s.deleting = true
    this.s.status = null
    try {
      const projectRecord = RemoteProject.instantiateFromResponse({id: String(projectId)})
      await projectRecord.destroy()
      await projectSyncLifecycle.retry()
      FlashNotifications.success(_("Project deleted"))
      if (this.projects.length === 1 && this.s.page > 1) {
        this.s.page -= 1
      }
    } catch (error) {
      FlashNotifications.errorResponse(error)
    } finally {
      this.s.deleting = false
    }
  }

  /**
   * Fails visibly before an unsupported offline command is created.
   * @returns {boolean} Whether online commands are currently allowed.
   */
  assertOnline() {
    if (this.online) return true
    const message = _("This action is only available while online")

    this.s.status = {kind: "error", text: message}
    FlashNotifications.error(message)
    return false
  }

  /**
   * Debounces project search input changes.
   * @param {string} value Search input value.
   * @returns {void} Nothing.
   */
  onSearchChangeText = (value) => {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
    this.searchDebounceTimer = setTimeout(() => {
      if (value !== this.s.searchQuery) {
        this.s.page = 1
        this.s.searchQuery = value
      }
    }, 300)
  }

  /**
   * Applies the selected project state filters.
   * @param {(string | number)[] | string | number | undefined} value Selected state filters.
   * @returns {void} Nothing.
   */
  onStateFilterChangeValue = (value) => {
    const stateFilters = parseSelectedStateFilters(value)
    if (sameStringArray(stateFilters, this.s.stateFilters)) return
    pushQueryParamUpdates(this.tt.router, this.tt.currentQueryParams(), "/projects", {
      state: serializeStateFilters(stateFilters)
    })
    this.s.page = 1
    this.s.stateFilters = stateFilters
  }

  /**
   * Applies a new page number.
   * @param {number} nextPage Page to load.
   * @returns {void} Nothing.
   */
  onPageChange = (nextPage) => {
    if (nextPage === this.s.page) return
    this.s.page = nextPage
  }

}

/** @type {NamedExoticComponent<ProjectsIndexScreenProps>} */
const ProjectsIndexScreenComponent = memo(shapeComponent(ProjectsIndexScreen))

export default ProjectsIndexScreenComponent

/**
 * Builds the stable local catalog filter used by page and count queries.
 * @param {{searchQuery: string, stateFilters: ProjectStateFilter[]}} args Filter values.
 * @returns {Record<string, unknown>} Local ransack parameters.
 */
function projectQueryParams({searchQuery, stateFilters}) {
  const params = {s: "name asc, id asc"}

  if (searchQuery) params.nameCont = searchQuery
  if (stateFilters.length > 0) params.stateIn = stateFilters

  return params
}

/**
 * Translates a project state label.
 * @param {(key: string) => string} _ Translation function.
 * @param {string} state Project state value.
 * @returns {string} Localized state label.
 */
function translateState(_, state) {
  if (state === "active") return _("Active")
  if (state === "finished") return _("Finished")
  if (state === "inactive") return _("Inactive")
  return state
}

/**
 * Builds project state filter options.
 * @returns {{text: string, value: string}[]} Select options.
 */
function projectStateFilterOptions() {
  return [
    {text: _("All states"), value: PROJECT_STATE_FILTER_ALL},
    {text: _("Active"), value: "active"},
    {text: _("Inactive"), value: "inactive"},
    {text: _("Finished"), value: "finished"}
  ]
}

/**
 * Reads project state filters from route params.
 * @param {RouteQueryParams} params Route params.
 * @returns {ProjectStateFilter[]} Selected filters, defaulting to active projects.
 */
function stateFiltersFromParams(params) {
  return parseProjectStateFilters(params.state)
}

/**
 * Parses HayaSelect values into project state filters.
 * @param {(string | number)[] | string | number | undefined} value Raw select callback value.
 * @returns {ProjectStateFilter[]} Concrete project states chosen by the select.
 */
function parseSelectedStateFilters(value) {
  const values = Array.isArray(value) ? value : value == null ? [] : [value]
  return parseProjectStateFilters(values)
}

/**
 * Parses route or select values into project state filters.
 * @param {unknown} value Raw filter value.
 * @returns {ProjectStateFilter[]} Selected filters, or an empty array for all states.
 */
function parseProjectStateFilters(value) {
  if (value == null || value === "") return [...DEFAULT_STATE_FILTERS]
  const values = Array.isArray(value) ? value : [value]
  /** @type {ProjectStateFilter[]} */
  const stateFilters = []
  for (const rawValue of values) {
    for (const stateValue of String(rawValue).split(",")) {
      if (stateValue === PROJECT_STATE_FILTER_ALL) return []
      if (isProjectStateFilter(stateValue) && !stateFilters.includes(stateValue)) {
        stateFilters.push(stateValue)
      }
    }
  }
  if (stateFilters.length === 0) return [...DEFAULT_STATE_FILTERS]
  return stateFilters
}

/**
 * Serializes state filters for the route.
 * @param {ProjectStateFilter[]} stateFilters Selected state filters.
 * @returns {string | undefined} Route param value.
 */
function serializeStateFilters(stateFilters) {
  if (stateFilters.length === 0) return PROJECT_STATE_FILTER_ALL
  if (sameStringArray(stateFilters, DEFAULT_STATE_FILTERS)) return undefined
  return stateFilters.join(",")
}

/**
 * Checks whether a string is a valid project state filter.
 * @param {string} value State value to inspect.
 * @returns {value is ProjectStateFilter} True when the value is a concrete project state.
 */
function isProjectStateFilter(value) {
  return value === "active" || value === "finished" || value === "inactive"
}

/**
 * Compares two ordered string arrays.
 * @param {string[]} first Earlier list in the comparison.
 * @param {string[]} second Later list in the comparison.
 * @returns {boolean} True when both arrays have the same values.
 */
function sameStringArray(first, second) {
  if (first.length !== second.length) return false
  for (let index = 0; index < first.length; index++) {
    if (first[index] !== second[index]) return false
  }
  return true
}
